<template>
  <div class="job-order-create">
    <div class="page-header">
      <button class="back-btn" @click="goBack" aria-label="Back to job orders">&#8249;</button>
      <div>
        <h1>Create Job Order Form</h1>
        <p>Fill in the details to create a new job order.</p>
      </div>
    </div>

    <div class="page-body">
      <div class="left-panel">
        <section class="card">
          <h2>Basic Information</h2>
          <div class="form-grid two-col">
            <div class="field">
              <label>Job Order Code *</label>
              <input v-model="form.jobOrderCode" type="text" readonly />
            </div>
            <div class="field">
              <label>Quantity *</label>
              <input v-model.number="form.quantity" type="number" min="1" />
            </div>
          </div>
          <div class="form-grid">
            <div class="field">
              <label>Material (with Process Plan) *</label>
              <select v-model="form.materialId">
                <option value="">Select material</option>
                <option v-for="material in materials" :key="material.id" :value="material.id">{{ material.name }}</option>
              </select>
            </div>
          </div>
          <div class="form-grid">
            <div class="field">
              <label>Factory *</label>
              <select v-model="form.factoryId" :disabled="!selectedMaterial">
                <option value="">Select factory</option>
                <option v-for="factory in factories" :key="factory.id" :value="factory.id">{{ factory.name }}</option>
              </select>
            </div>
          </div>
          <p v-if="selectedFactory" class="capacity-line">Capacity: {{ selectedFactory.monthlyCapacity }} units/month</p>
        </section>

        <section v-if="selectedMaterial" class="card">
          <div class="card-head">
            <h2>Attach BOM (Optional)</h2>
            <span class="tag">Supports Nested BOMs</span>
          </div>
          <div class="form-grid">
            <div class="field">
              <label>Select BOM</label>
              <select v-model="form.bomId">
                <option value="">Select BOM to attach</option>
                <option v-for="bom in selectedMaterial.boms" :key="bom.id" :value="bom.id">{{ bom.name }}</option>
              </select>
            </div>
          </div>
          <button v-if="form.bomId" type="button" class="flatten-btn" @click="toggleFlattenPreview">
            <span class="flatten-icon"></span>
            {{ bomFlattened ? 'Close Preview' : 'Flatten BOM & Preview' }}
          </button>

          <div v-if="bomFlattened" class="flat-preview">
            <div class="flat-note">
              <p class="flat-title">BOM Flattening Complete</p>
              <p>{{ flattenedBomRows.length }} materials identified. Process plans and purchase requests are generated.</p>
            </div>
            <div class="flat-table-wrap">
              <table class="flat-table">
                <thead>
                  <tr><th>Material</th><th>Type</th><th>Qty</th><th>Action</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in flattenedBomRows" :key="row.material + idx">
                    <td><div class="cell-main">{{ row.material }}</div><div class="cell-sub">{{ row.code }}</div></td>
                    <td><span class="type-pill" :class="row.type.toLowerCase()">{{ row.type }}</span></td>
                    <td>{{ row.qty }}</td>
                    <td><div class="cell-main">{{ row.action }}</div><div class="cell-sub">{{ row.leadTime }}</div></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div class="actions">
          <button class="btn btn-cancel mr-2" @click="goBack">Cancel</button>
          <button class="btn" @click="createJobOrder">Create Job Order &amp; Execute</button>
        </div>
      </div>

      <div class="right-panel">
        <div class="panel-title">Capacity Feasibility Check</div>
        <template v-if="showFeasibilityPanel">
          <section class="feasibility-card" :class="summaryStatusClass">
            <div class="summary-top">
              <span class="summary-icon">{{ summaryIcon }}</span>
              <h2>{{ summaryTitle }}</h2>
            </div>
            <p class="factory-name">Factory: {{ selectedFactory.shortFactoryName }}</p>
            <div class="summary-grid">
              <div><span class="k">Order Quantity</span><strong>{{ form.quantity }} units</strong></div>
              <div><span class="k">Estimated Completion</span><strong>{{ selectedFeasibility.estimatedCompletion }}</strong></div>
              <div><span class="k">Due Date</span><strong>{{ selectedFeasibility.dueDate }}</strong></div>
            </div>
            <p class="status-line">{{ summaryLine }}</p>
          </section>

          <section class="card analysis-card">
            <h2>Detailed Capacity Analysis</h2>
            <div v-for="check in selectedFeasibility.checks" :key="check.key" class="analysis-row">
              <div class="row-icon" :class="check.status">{{ checkIcon(check.status) }}</div>
              <div class="analysis-main">
                <p class="row-title">{{ check.title }}</p>
                <p class="row-values">Available: {{ check.available }} | Required: {{ check.required }}</p>
                <p class="row-note">{{ check.note }}</p>
              </div>
              <div class="analysis-side">
                <span class="pill" :class="check.status">{{ check.status.toUpperCase() }}</span>
                <span class="capacity-value">{{ check.capacity }}</span>
                <span class="capacity-label">Capacity</span>
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const CHECK_PROFILES = {
  pass: [
    { key: 'machine', title: 'Available Machine Time', available: '220 hours', required: '130 hours', note: 'Machine availability is healthy', status: 'pass', capacity: '169%' },
    { key: 'assembly', title: 'Assembly Line Capacity', available: '64 units/day', required: '45 units/day', note: 'Assembly lines are stable', status: 'pass', capacity: '142%' },
    { key: 'tooling', title: 'Tool / Fixture Availability', available: '9 fixtures', required: '6 fixtures', note: 'Tooling ready with backup', status: 'pass', capacity: '150%' },
    { key: 'queue', title: 'Current Bookings / Queue', available: '12 slots', required: '10 slots', note: 'Queue can absorb this order', status: 'pass', capacity: '120%' },
    { key: 'labor', title: 'Labor Availability', available: '15 workers', required: '11 workers', note: 'Skilled workers available', status: 'pass', capacity: '136%' }
  ],
  risky: [
    { key: 'machine', title: 'Available Machine Time', available: '200 hours', required: '150 hours', note: 'CNC machine heavily booked', status: 'warning', capacity: '133%' },
    { key: 'assembly', title: 'Assembly Line Capacity', available: '55 units/day', required: '50 units/day', note: 'Running at 91% capacity', status: 'warning', capacity: '110%' },
    { key: 'tooling', title: 'Tool / Fixture Availability', available: '7 fixtures', required: '6 fixtures', note: 'Minimal fixtures available', status: 'pass', capacity: '117%' },
    { key: 'queue', title: 'Current Bookings / Queue', available: '8 slots', required: '10 slots', note: '4 orders ahead in queue', status: 'warning', capacity: '80%' },
    { key: 'labor', title: 'Labor Availability', available: '13 workers', required: '12 workers', note: 'May need overtime', status: 'warning', capacity: '108%' }
  ],
  fail: [
    { key: 'machine', title: 'Available Machine Time', available: '100 hours', required: '140 hours', note: 'Machines fully booked', status: 'fail', capacity: '71%' },
    { key: 'assembly', title: 'Assembly Line Capacity', available: '35 units/day', required: '45 units/day', note: 'Assembly overloaded', status: 'fail', capacity: '78%' },
    { key: 'tooling', title: 'Tool / Fixture Availability', available: '4 fixtures', required: '6 fixtures', note: 'Insufficient fixtures', status: 'fail', capacity: '67%' },
    { key: 'queue', title: 'Current Bookings / Queue', available: '3 slots', required: '10 slots', note: 'Heavy queue backlog', status: 'fail', capacity: '30%' },
    { key: 'labor', title: 'Labor Availability', available: '8 workers', required: '12 workers', note: 'Staff shortage', status: 'fail', capacity: '67%' }
  ]
}

export default {
  name: 'JobOrderCreate',
  data () {
    return {
      form: { jobOrderCode: 'JO-2025-XXX', quantity: 1, materialId: '', factoryId: '', bomId: '' },
      bomFlattened: false,
      materials: [
        { id: 'MAT-GYS-5L', name: 'MAT-GYS-5L - Geyser 5L Complete Unit', boms: [{ id: 'BOM-GYS-5L-V1', name: 'BOM-GYS-5L-V1 - Geyser 5L Complete BOM' }] },
        { id: 'MAT-VLV-001', name: 'MAT-VLV-001 - Pressure Relief Valve', boms: [{ id: 'BOM-PRV-V1', name: 'BOM-PRV-V1 - Pressure Valve Sub-Assembly BOM' }] },
        { id: 'MAT-MTR-2HP', name: 'MAT-MTR-2HP - Motor Drive Assembly', boms: [{ id: 'BOM-MTR-2HP-V1', name: 'BOM-MTR-2HP-V1 - Motor Drive Assembly BOM' }] }
      ],
      factories: [
        { id: 'FAC-MH-01', name: 'FAC-MH-01 - Maharashtra Plant - Pune', shortFactoryName: 'Maharashtra Plant - Pune', monthlyCapacity: 500 },
        { id: 'FAC-TN-01', name: 'FAC-TN-01 - Tamil Nadu Plant - Chennai', shortFactoryName: 'Tamil Nadu Plant - Chennai', monthlyCapacity: 600 },
        { id: 'FAC-KA-01', name: 'FAC-KA-01 - Karnataka Plant - Bangalore', shortFactoryName: 'Karnataka Plant - Bangalore', monthlyCapacity: 400 },
        { id: 'FAC-GJ-01', name: 'FAC-GJ-01 - Gujarat Plant - Ahmedabad', shortFactoryName: 'Gujarat Plant - Ahmedabad', monthlyCapacity: 750 }
      ],
      feasibilityMap: {
        'MAT-GYS-5L|FAC-MH-01': { estimatedCompletion: '10 days', dueDate: '25 May 2026', profile: 'pass' },
        'MAT-GYS-5L|FAC-TN-01': { estimatedCompletion: '11 days', dueDate: '25 Jun 2026', profile: 'risky' },
        'MAT-GYS-5L|FAC-KA-01': { estimatedCompletion: '18 days', dueDate: '25 July 2026', profile: 'fail' },
        'MAT-GYS-5L|FAC-GJ-01': { estimatedCompletion: '12 days', dueDate: '25 Apr 2026', profile: 'risky' },
        'MAT-VLV-001|FAC-MH-01': { estimatedCompletion: '7 days', dueDate: '25 Jan 2026', profile: 'pass' },
        'MAT-VLV-001|FAC-TN-01': { estimatedCompletion: '9 days', dueDate: '25 Oct 2026', profile: 'risky' },
        'MAT-VLV-001|FAC-KA-01': { estimatedCompletion: '15 days', dueDate: '25 Sep 2026', profile: 'fail' },
        'MAT-VLV-001|FAC-GJ-01': { estimatedCompletion: '14 days', dueDate: '25 Nov 2026', profile: 'risky' },
        'MAT-MTR-2HP|FAC-MH-01': { estimatedCompletion: '13 days', dueDate: '25 Oct 2026', profile: 'risky' },
        'MAT-MTR-2HP|FAC-TN-01': { estimatedCompletion: '12 days', dueDate: '25 Nov 2026', profile: 'pass' },
        'MAT-MTR-2HP|FAC-KA-01': { estimatedCompletion: '17 days', dueDate: '25 Nov 2026', profile: 'fail' },
        'MAT-MTR-2HP|FAC-GJ-01': { estimatedCompletion: '11 days', dueDate: '25 Nov 2026', profile: 'risky' }
      },
      flattenedBomById: {
        'BOM-GYS-5L-V1': [
          { material: 'Water Tank 5L', code: 'MAT-TNK-5L', type: 'Mfg', qty: '1 Unit', action: 'Tank Fabrication Process', leadTime: '4 steps' },
          { material: 'SS 304 Sheet', code: 'MAT-SS-304', type: 'Buy', qty: '2.5 Kg', action: 'Purchase Request', leadTime: '5 days' },
          { material: 'Heating Element 2KW', code: 'MAT-HTR-2KW', type: 'Buy', qty: '1 Unit', action: 'Purchase Request', leadTime: '10 days' },
          { material: 'Pressure Relief Valve', code: 'MAT-VLV-001', type: 'Buy', qty: '1 Unit', action: 'Purchase Request', leadTime: '7 days' }
        ],
        'BOM-PRV-V1': [
          { material: 'Valve Body Casting', code: 'MAT-VLV-BDY', type: 'Mfg', qty: '1 Unit', action: 'CNC Machining Process', leadTime: '3 steps' },
          { material: 'Spring Assembly', code: 'MAT-SPR-ASSY', type: 'Buy', qty: '1 Unit', action: 'Purchase Request', leadTime: '4 days' },
          { material: 'Rubber Seal Kit', code: 'MAT-SEAL-KIT', type: 'Buy', qty: '1 Set', action: 'Purchase Request', leadTime: '3 days' },
          { material: 'Thread Lock Compound', code: 'MAT-TLK-01', type: 'Buy', qty: '1 Pack', action: 'Purchase Request', leadTime: '2 days' }
        ],
        'BOM-MTR-2HP-V1': [
          { material: 'Motor Housing', code: 'MAT-MTR-HSG', type: 'Mfg', qty: '1 Unit', action: 'Casting + Finish Process', leadTime: '4 steps' },
          { material: 'Stator Assembly', code: 'MAT-STR-ASY', type: 'Mfg', qty: '1 Unit', action: 'Winding Process', leadTime: '3 steps' },
          { material: 'Bearing Set', code: 'MAT-BRG-SET', type: 'Buy', qty: '2 Units', action: 'Purchase Request', leadTime: '6 days' },
          { material: 'Mounting Bolts', code: 'MAT-BLT-M8', type: 'Buy', qty: '6 Units', action: 'Purchase Request', leadTime: '2 days' }
        ]
      }
    }
  },
  computed: {
    selectedMaterial () { return this.materials.find(material => material.id === this.form.materialId) || null },
    selectedFactory () { return this.factories.find(factory => factory.id === this.form.factoryId) || null },
    selectedFeasibility () {
      const base = this.feasibilityMap[`${this.form.materialId}|${this.form.factoryId}`]
      if (!base) return null
      return { ...base, checks: CHECK_PROFILES[base.profile] }
    },
    flattenedBomRows () { return this.form.bomId ? (this.flattenedBomById[this.form.bomId] || []) : [] },
    showFeasibilityPanel () { return Boolean(this.selectedFactory && this.selectedFeasibility) },
    summaryStatus () {
      if (!this.selectedFeasibility) return 'none'
      const checks = this.selectedFeasibility.checks
      if (checks.some(check => check.status === 'fail')) return 'fail'
      if (checks.some(check => check.status === 'warning')) return 'warning'
      return 'pass'
    },
    summaryTitle () {
      const titleMap = { pass: 'Order is Feasible', warning: 'Order is Risky - Monitor Closely', fail: 'Order is Not Feasible' }
      return titleMap[this.summaryStatus] || ''
    },
    summaryLine () {
      const lineMap = { pass: 'Can meet deadline', warning: 'Can meet deadline', fail: 'Cannot meet deadline - consider extending or reducing quantity' }
      return lineMap[this.summaryStatus] || ''
    },
    summaryIcon () {
      const iconMap = { pass: '\u2713', warning: '!', fail: '\u00D7' }
      return iconMap[this.summaryStatus] || ''
    },
    summaryStatusClass () {
      const classMap = { pass: 'feasible', warning: 'risky', fail: 'not-feasible' }
      return classMap[this.summaryStatus] || ''
    }
  },
  watch: {
    'form.materialId' () { this.form.factoryId = ''; this.form.bomId = ''; this.bomFlattened = false },
    'form.bomId' () { this.bomFlattened = false }
  },
  methods: {
    checkIcon (status) { if (status === 'fail') return '\u00D7'; if (status === 'warning') return '!'; return '\u2713' },
    toggleFlattenPreview () {
      this.bomFlattened = !this.bomFlattened
      if (this.bomFlattened) this.$notify.success('Bom flattened successfully!')
    },
    goBack () {
      this.$router.push('/job-orders')
    },
    createJobOrder () {
      if (!this.form.materialId || !this.form.factoryId || !this.form.quantity) return this.$notify.alert('Please fill all required fields')
      this.$router.push({ path: '/job-orders', query: { created: '1' } })
    }
  }
}
</script>

<style scoped lang="scss">
.job-order-create { min-height: 100%; overflow: visible; background: #f8f8f8; font-size: var(--text-xs2); }
.page-header { display: flex; align-items: center; gap: 10px; padding: 12px 20px; border-bottom: 1px solid #e4e4e4; background: #fff; }
.back-btn { border: none; background: transparent; color: #2f2f2f; font-size: var(--text-base); line-height: 1; cursor: pointer; padding: 0 4px; }
h1 { margin: 0; font-size: var(--text-base); font-weight: 500; color: #18253d; }
.page-header p { margin: 2px 0 0; font-size: var(--text-xs2); color: #5b6471; }
.page-body { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; padding: 16px 20px; align-items: start; }
.card, .feasibility-card { border: 1px solid #dedede; border-radius: 8px; background: #fff; padding: 12px; }
h2 { margin: 0 0 12px; font-size: var(--text-base); font-weight: 600; color: #111827; }
.panel-title { margin: 0 0 10px; font-size: var(--text-base); font-weight: 600; color: #0f172a; }
.form-grid { display: grid; gap: 8px; }
.form-grid.two-col { grid-template-columns: 1fr 1fr; }
.field label { display: block; font-size: var(--text-s); color: #111827; margin-bottom: 5px; font-weight: 500; }
.field input, .field select { width: 100%; height: 38px; padding: 0 10px; border: 1px solid #e4e4e7; border-radius: 6px; background: #f8f8f8; color: #374151; font-size: var(--text-s); }
.capacity-line { margin: 8px 0 0; font-size: var(--text-xs2); color: #334155; }
.card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.card-head h2 { margin: 0; }
.tag { background: #e6efff; color: #2656bd; font-size: var(--text-xs2); padding: 4px 8px; border-radius: 4px; font-weight: 500; }
.flatten-btn { width: 100%; margin-top: 8px; border: 1px solid #d2d6dc; border-radius: 4px; background: #fff; color: #111827; height: 34px; font-size: var(--text-s); display: inline-flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer; }
.flat-preview { margin-top: 8px; }
.flat-note { border: 1px solid #9ec5ff; background: #eef5ff; border-radius: 6px; padding: 10px; margin-bottom: 8px; }
.flat-title { margin: 0 0 5px; color: #1d4ed8; font-size: var(--text-s); font-weight: 600; }
.flat-note p { margin: 0; color: #1e3a8a; font-size: var(--text-xs2); }
.flat-table-wrap { border: 1px solid #e5e7eb; border-radius: 6px; overflow: auto; }
.flat-table { width: 100%; border-collapse: collapse; font-size: var(--text-xs2); }
.flat-table th, .flat-table td { border-bottom: 1px solid #edf0f3; text-align: left; padding: 7px; white-space: nowrap; }
.flat-table th { background: #f8fafc; font-size: var(--text-xs2); font-weight: 600; color: #334155; }
.cell-main { color: #111827; font-size: var(--text-xs2); }
.cell-sub { color: #64748b; font-size: var(--text-xs1); }
.type-pill { display: inline-flex; align-items: center; border-radius: 3px; font-size: var(--text-xs2); padding: 2px 6px; font-weight: 600; }
.type-pill.mfg { background: #dbeafe; color: #1d4ed8; }
.type-pill.buy { background: #dcfce7; color: #15803d; }
.actions { display: flex; justify-content: flex-end; margin-top: 10px; }
.summary-top { display: flex; align-items: center; gap: 8px; }
.summary-top h2 { margin: 0; }
.summary-icon { width: 16px; height: 16px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: var(--text-xs); font-weight: 700; }
.feasibility-card { margin-bottom: 12px; }
.feasibility-card.feasible { background: #eff9f1; border-color: #9dddb0; .summary-icon { border: 1px solid #16a34a; color: #16a34a; } }
.feasibility-card.risky { background: #faf7e8; border-color: #f4d35e; .summary-icon { border: 1px solid #d98b00; color: #d98b00; } }
.feasibility-card.not-feasible { background: #fff1f1; border-color: #f1b4b4; .summary-icon { border: 1px solid #ef4444; color: #ef4444; } }
.factory-name { margin: 6px 0 10px; color: #1f2937; font-size: var(--text-s); }
.summary-grid { display: grid; grid-template-columns: repeat(3, minmax(110px, 1fr)); gap: 8px; }
.summary-grid .k { display: block; font-size: var(--text-xs2); color: #4b5563; margin-bottom: 2px; }
.summary-grid strong { font-size: var(--text-s); color: #111827; }
.status-line { margin: 8px 0 0; font-size: var(--text-xs2); font-weight: 600; }
.feasibility-card.feasible .status-line, .feasibility-card.risky .status-line { color: #0f7a3e; }
.feasibility-card.not-feasible .status-line { color: #b91c1c; }
.analysis-card h2 { margin-bottom: 10px; }
.analysis-row { border: 1px solid #ececec; border-radius: 7px; padding: 10px; display: flex; align-items: flex-start; gap: 10px; background: #fbfbfb; }
.analysis-row + .analysis-row { margin-top: 8px; }
.row-icon { width: 16px; height: 16px; min-width: 16px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: var(--text-xs); font-weight: 700; margin-top: 2px; }
.row-icon.pass { border: 1px solid #16a34a; color: #16a34a; }
.row-icon.warning { border: 1px solid #d98b00; color: #d98b00; }
.row-icon.fail { border: 1px solid #ef4444; color: #ef4444; }
.analysis-main { flex: 1; }
.row-title { margin: 0; font-weight: 600; font-size: var(--text-s); color: #111827; }
.row-values { margin: 2px 0; color: #334155; font-size: var(--text-xs2); }
.row-note { margin: 0; color: #6b7280; font-size: var(--text-xs1); }
.analysis-side { min-width: 84px; text-align: right; }
.pill { display: inline-block; border-radius: 4px; padding: 2px 8px; font-size: var(--text-xs); font-weight: 700; }
.pill.pass { background: #dff5e5; color: #177f41; }
.pill.warning { background: #fcefc8; color: #9a6a00; }
.pill.fail { background: #ffe2e2; color: #bc1e1e; }
.capacity-value { display: block; margin-top: 6px; font-size: var(--text-s); color: #111827; font-weight: 600; }
.capacity-label { color: #64748b; font-size: var(--text-xs1); }
.job-order-create .btn { font-size: var(--text-s); }
@media (max-width: 1320px) { .page-body { grid-template-columns: 1fr; } }
@media (max-width: 900px) {
  .page-header { padding: 10px 12px; }
  .page-body { padding: 10px 12px; gap: 12px; }
  .form-grid.two-col, .summary-grid { grid-template-columns: 1fr; }
  .summary-grid { gap: 6px; }
  .analysis-side { min-width: 70px; }
}
</style>
